/**
 * What kind of shop section a page belongs to, and what that implies.
 *
 * Torsbo's feedback on the redesign ("Hemsidan 2.0") is largely per-section:
 * weapons get neither free shipping nor an open purchase, and ammunition needs
 * its own licence wording. Those are different questions with one answer —
 * "which section is this?" — so they live together here rather than as sets of
 * conditionals spread across the product page.
 *
 * Price bands used to live here too. They are gone: enabling layered navigation
 * on the price attribute made Magento publish a `price` aggregation, so the
 * filter reads real per-category bounds instead of a hardcoded list.
 *
 * Adding a section later is one entry in `SECTIONS` plus its translation keys.
 * No component branches on a category name, which is what keeps this scalable:
 * components ask the registry, the registry decides.
 */

/**
 * One of the trust badges under the delivery heading.
 *
 * "Hemsidan 3.0" p.3 replaced the pair that shippable products used to carry —
 * `freeShipping` and `thirtyDayReturns` — with these two. The old keys are gone
 * rather than left unused: a promise nothing draws is a promise nobody has
 * checked is still true.
 */
export type BadgeKey = 'fastShipping' | 'securePayment' | 'helpWithLicenseApplication';

export interface Section {
  id: string;
  /**
   * Top-level category url_keys that belong to this section. Matched against
   * the FIRST path segment of a category path, so every descendant is covered
   * without listing it.
   */
  slugs: string[];
  /** Which trust badges are true here. An empty list draws none. */
  badges: BadgeKey[];
  /**
   * Key under `product.license` for the notice above the buy button. Omitted
   * where the section has no licence wording of its own, in which case the
   * weapon default is used — the notice only ever renders for a product
   * Magento has flagged `th_licenspliktig` anyway.
   */
  licenseKey?: string;
  /** Key under `product` for the delivery paragraph of an unlicensed product. */
  deliveryKey: string;
  /**
   * Key under `product` for the delivery paragraph when the product IS licence-
   * bound. Defaults to the weapon copy, which is what a licensed barrel or bolt
   * under `vapentillbehor` needs — p.3 prices those parts explicitly and states
   * they are only handed over against a licence.
   */
  licensedDeliveryKey?: string;
}

/**
 * The sections, in resolution priority order.
 *
 * Order matters: a product assigned to both `vapen` and `ammunition` (a package
 * with cartridges, say) resolves to the FIRST match here, not to whichever
 * category Magento happened to return first. `default` is last and matches
 * nothing by slug.
 *
 * Slugs are the live top-level url_keys. Campaign categories (`kampanj`,
 * `winter-deal`, `popular-just-nu`, `nyheter`, `gift`) are deliberately absent:
 * a rifle on campaign is still a rifle, and letting a campaign category win
 * would strip its licence notice.
 */
export const SECTIONS: Section[] = [
  {
    id: 'vapen',
    slugs: ['vapen', 'vapenpaket'],
    // "Hemsidan 2.0" p.3: "Fri Frakt: NEJ / 30 dagars öppet köp: NEJ" — an
    // answer about shipping and returns, not about paying. `securePayment` is
    // listed because it is true here: a rifle is bought through the same
    // checkout as a jacket.
    //
    // `helpWithLicenseApplication` is declared on the SECTION, not derived from
    // `th_licenspliktig`, so every product filed here carries it whether or not
    // Magento flags the individual item. 2 of 600 sampled weapons carry no
    // flag; they still need the notice. Do not move this onto the flag.
    badges: ['securePayment', 'helpWithLicenseApplication'],
    licenseKey: 'body',
    deliveryKey: 'delivery.vapen',
  },
  {
    id: 'ammunition',
    slugs: ['ammunition'],
    // Ammunition ships as a dangerous good and is not returnable once opened,
    // so it carries the weapon side of the promise rather than the clothing
    // one — but paying for it is no different. Section-declared licence help,
    // for the reason given on `vapen` above.
    badges: ['securePayment', 'helpWithLicenseApplication'],
    // p.9: "Ammunition lämnas endast ut mot uppvisad originallicens och giltig
    // legitimation" — a different requirement from a weapon's.
    licenseKey: 'bodyAmmunition',
    deliveryKey: 'delivery.ammunition',
    licensedDeliveryKey: 'delivery.ammunition',
  },
  {
    // Reloading components. Filed with ammunition until "Hemsidan 3.0" p.4
    // asked what drove the delivery copy, with a Hornady case tumbler showing
    // ammunition's licence wording — the whole category inherited it, and not
    // one of its 273 products is flagged `th_licenspliktig`.
    //
    // The exception the client describes — "bara tändhattar och krut som ej ska
    // vara leveransbara" — needs nothing from this section: krut (10 of 10) and
    // tändhattar (3 of 3) already carry `th_skickas_ej`, which drives the pickup
    // copy per product. Putting it in the section instead would catch the
    // bullets and the dies too, which is the mistake being undone here.
    id: 'handladdning',
    slugs: ['handladdning'],
    badges: ['fastShipping', 'securePayment'],
    deliveryKey: 'delivery.standard',
  },
  {
    id: 'optik',
    slugs: ['kikarsikten'],
    badges: ['fastShipping', 'securePayment'],
    deliveryKey: 'delivery.standard',
  },
  {
    id: 'jaktklader',
    slugs: ['jaktklader'],
    // p.8: "Ta bort Vi hjälper dig med licensansökan här".
    badges: ['fastShipping', 'securePayment'],
    deliveryKey: 'delivery.standard',
  },
  {
    id: 'tillbehor',
    slugs: ['vapentillbehor', 'jakt', 'skytte'],
    badges: ['fastShipping', 'securePayment'],
    deliveryKey: 'delivery.standard',
  },
  {
    id: 'default',
    slugs: [],
    badges: ['fastShipping', 'securePayment'],
    deliveryKey: 'delivery.standard',
  },
];

/** The fallback section, used when no slug matches. */
const DEFAULT_SECTION = SECTIONS[SECTIONS.length - 1];

/**
 * Whether a category path sits under `slug`.
 *
 * The path is an ancestor chain built by the adapter (`ammunition/22lr-ammunition`)
 * with the store's URL suffix appended to the END of the whole path — so a
 * top-level-only assignment is the one case where the suffix lands on the
 * segment being matched, hence the `.` case.
 *
 * Deliberately NOT implemented by stripping everything after the last ".":
 * this catalogue has url_keys that genuinely contain dots (".308win"), and that
 * would mangle them.
 */
function underSlug(path: string, slug: string): boolean {
  return path === slug || path.startsWith(`${slug}/`) || path.startsWith(`${slug}.`);
}

/**
 * The section a single category path belongs to, or undefined when it belongs
 * to none — a campaign category, or a section nobody has declared yet.
 */
export function sectionOf(categoryPath: string): Section | undefined {
  const path = categoryPath.trim();
  if (!path) return undefined;
  return SECTIONS.find((section) => section.slugs.some((slug) => underSlug(path, slug)));
}

/**
 * The section for a product, given every category path it carries.
 *
 * Products are assigned to campaign categories alongside their real ones, so
 * this scans all of them and lets `SECTIONS` order decide rather than taking
 * the first path — see the note on SECTIONS.
 */
export function resolveSection(categoryPaths: readonly string[]): Section {
  for (const section of SECTIONS) {
    for (const path of categoryPaths) {
      const trimmed = path.trim();
      if (trimmed && section.slugs.some((slug) => underSlug(trimmed, slug))) return section;
    }
  }
  return DEFAULT_SECTION;
}

/** Badges that claim something about shipping, and so cannot be true of a
 * product that is never shipped. */
const SHIPPING_BADGES: readonly BadgeKey[] = ['fastShipping'];

/**
 * The trust badges to draw.
 *
 * A licence-bound product claims no shipping promise regardless of the section
 * it sits in: "Hemsidan 2.0" p.3 answers "Fri Frakt: NEJ / 30 dagars öppet köp:
 * NEJ" for weapons, and a licensed barrel filed under accessories is subject to
 * the same handover rules. It claims everything else the section does, though —
 * that p.3 answer was about shipping and returns, and says nothing about how
 * the customer pays.
 *
 * `pickupOnly` (`th_skickas_ej`) is narrower and additive: the product is
 * collected in the shop, so a shipping claim on it is false, but everything
 * else the section promises still holds. Only the shipping badges go — "Trygg
 * betalning" is as true over the counter as it is in the post. This used to
 * reach the delivery paragraph and nothing else, which left an 80 kg gun safe
 * (SKU 1824213, Vapentillbehör) saying "hämtas i butik" and "Snabb frakt" in
 * the same panel.
 */
export function sectionBadges(
  section: Section,
  { licensed = false, pickupOnly = false, notSellableOnline = false }: ProductConditions = {},
): BadgeKey[] {
  // All three flags say the same thing about shipping and nothing about
  // anything else, so each subtracts the same badges and leaves the rest
  // standing. `notSellableOnline` counts on its own account, not only through
  // the pickup flag it usually accompanies: 45 of those products sit in
  // sections that do promise shipping. The
  // licensed case used to REPLACE the whole list with licence help, which was
  // right while the section promises were "Fri frakt" and "30 dagars öppet
  // köp" — two claims a licence-bound product cannot make — and quietly wrong
  // once they became "Snabb frakt" and "Trygg betalning".
  const badges =
    licensed || pickupOnly || notSellableOnline
      ? section.badges.filter((b) => !SHIPPING_BADGES.includes(b))
      : section.badges;

  // A licensed product in a section that does not declare licence help — a
  // licensed barrel filed under accessories — still needs it.
  if (licensed && !badges.includes('helpWithLicenseApplication')) {
    return [...badges, 'helpWithLicenseApplication'];
  }

  return badges;
}

/** Key under `product.license` for this section's notice. */
export function licenseCopyKey(section: Section): string {
  return section.licenseKey ?? 'body';
}

/**
 * Key under `product` for the delivery paragraph.
 *
 * Same override as the badges: being licence-bound decides the copy, and the
 * section only chooses WHICH licensed copy — ammunition states a different
 * requirement from a firearm (p.9 vs p.3).
 *
 * Kept for the single-paragraph case; `deliveryCopyKeys` is what the panel
 * renders, because a product can be subject to two conditions at once.
 */
export function deliveryCopyKey(section: Section, licensed = false): string {
  if (licensed) return section.licensedDeliveryKey ?? 'delivery.vapen';
  return section.deliveryKey;
}

/** The product-level conditions that change what the delivery panel says. */
export interface ProductConditions {
  /** `th_licenspliktig` — handover requires a licence. */
  licensed?: boolean;
  /** `th_skickas_ej` — the product is never shipped. */
  pickupOnly?: boolean;
  /**
   * `status_text` says the ERP will not sell it online at all.
   *
   * Outranks `pickupOnly`: collection copy describes how an ONLINE order is
   * handed over, and there is no order to hand over. 353 of the 354 products
   * carrying this status also carry `th_skickas_ej`, so the two meet on nearly
   * every one of them.
   */
  notSellableOnline?: boolean;
}

/**
 * Every paragraph the delivery panel should state, in reading order.
 *
 * A list rather than one key because the two conditions are INDEPENDENT and a
 * product can carry both. Ammunition is the case that forced it: 411 of the
 * catalogue's 418 ammunition products are licence-bound AND never shipped, and
 * pickup-only used to replace the section's copy outright — so those pages were
 * headed "Leverans & licens" and then said nothing at all about a licence,
 * which is the one fact a customer must not miss.
 *
 * Note what this deliberately does NOT do: it never drops the section's own
 * copy for an unflagged product. Ammunition declares the same key for both
 * `deliveryKey` and `licensedDeliveryKey` precisely so that every product in
 * the section carries the licence wording whether or not Magento flags it, and
 * that rule survives here untouched.
 *
 * No weapon in the catalogue is pickup-only today (600 sampled, plus all 85
 * vapenpaket), so pairing the pickup line with `delivery.vapen` — which quotes
 * shipping prices — is currently unreachable. If a pickup-only weapon ever
 * appears, that copy needs splitting rather than this rule changing.
 */
export function deliveryCopyKeys(
  section: Section,
  { licensed = false, pickupOnly = false, notSellableOnline = false }: ProductConditions,
): string[] {
  const keys: string[] = [];

  // Not both: the collection paragraph says we will have the ORDER ready, and
  // a product that cannot be ordered online has no order to put aside. The
  // licence terms below are a different fact and survive either way.
  if (notSellableOnline) keys.push('delivery.notSellableOnline');
  else if (pickupOnly) keys.push('delivery.pickupOnly');

  if (licensed) keys.push(section.licensedDeliveryKey ?? 'delivery.vapen');

  // Neither condition applies, so the section speaks for itself.
  return keys.length > 0 ? keys : [section.deliveryKey];
}

/**
 * The delivery accordion's heading key.
 *
 * p.8 asks for a plain "Leverans" on a hunting overall. Deliberately takes no
 * section: the licence half of the heading is earned by the PRODUCT, not by
 * where it is filed — a licensed item in a clothing category still has licence
 * terms to state, and an unlicensed one in the weapon category does not.
 */
export function deliveryTitleKey(licensed: boolean): string {
  return licensed ? 'delivery.title' : 'delivery.titlePlain';
}
